import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/lib/supabase';
import { usePregnancyStore } from '@/store/pregnancyStore';
import { useAuth } from '@/features/auth';
import { useLimits } from '@/hooks/useLimits';
import { isValidUUID } from '@/lib/utils';
import type { BabyName, NameFilters, NameMatch } from '../types';

const DEFAULT_FILTERS: NameFilters = {
  gender: 'all',
  origins: [],
  startingLetter: null,
  popularityMax: null,
};

// Wie viele Namen pro Server-Anfrage geladen werden
const BATCH_SIZE = 50;

// Ab wie wenigen verbleibenden Namen im State nachgeladen wird
const PREFETCH_THRESHOLD = 10;

// Fehlermeldung des BEFORE-INSERT-Triggers auf name_swipes
// (Migration 160_swipe_limit_enforcement.sql)
const SWIPE_LIMIT_ERROR = 'SWIPE_LIMIT_REACHED';

/** Antwort von get_swipe_status (Migration 160) */
interface SwipeStatus {
  used_today: number;
  limit_value: number;
  is_unlimited: boolean;
  remaining: number | null;
  resets_at: string;
}

export function useNames() {
  const { user } = useAuth();
  const { currentPregnancy, lastCompletedPregnancy, appMode } = usePregnancyStore();
  const { isLoading: isLoadingLimits, error: limitsError } = useLimits();

  // In baby mode, use lastCompletedPregnancy; otherwise use currentPregnancy
  const isBabyMode = appMode === 'baby' && lastCompletedPregnancy?.completion_type === 'birth';
  const activePregnancy = currentPregnancy || (isBabyMode ? lastCompletedPregnancy : null);
  const rawPregnancyId = activePregnancy?.id;

  // Validate that the pregnancy ID is a valid UUID before using it
  const activePregnancyId = isValidUUID(rawPregnancyId) ? rawPregnancyId : null;

  // Kontext, in dem geswipt wird - muss beim Lesen und Schreiben gleich sein
  const contextType = isBabyMode ? 'baby' : 'pregnancy';

  const [names, setNames] = useState<BabyName[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [filters, setFilters] = useState<NameFilters>(DEFAULT_FILTERS);
  const [swipeCount, setSwipeCount] = useState(0);
  const [newMatch, setNewMatch] = useState<NameMatch | null>(null);

  // Swipe-Stand kommt aus get_swipe_status - Tageszaehlung und Reset laufen
  // dort in der Zeitzone des Nutzers, nicht mehr clientseitig in UTC.
  const [swipeStatus, setSwipeStatus] = useState<SwipeStatus | null>(null);

  // Wird gesetzt, wenn der Server den Swipe mit SWIPE_LIMIT_REACHED ablehnt
  const [limitBlocked, setLimitBlocked] = useState(false);

  // true, solange der Server noch weitere Namen liefern kann
  const [hasMoreFromServer, setHasMoreFromServer] = useState(true);

  // Verhindert parallele Nachlade-Anfragen
  const isLoadingMoreRef = useRef(false);

  // Limit-Werte aus dem Server-Status
  const swipeLimit = swipeStatus?.limit_value ?? 0;
  const hasUnlimitedSwipes = swipeStatus?.is_unlimited ?? false;
  const dailySwipeCount = swipeStatus?.used_today ?? 0;
  const resetsAt = swipeStatus?.resets_at ?? null;
  const isLimitReached =
    limitBlocked || (!!swipeStatus && !hasUnlimitedSwipes && dailySwipeCount >= swipeLimit);
  const remainingSwipes = hasUnlimitedSwipes
    ? Infinity
    : (swipeStatus?.remaining ?? 0);

  // Tagesstand vom Server holen (Zeitzone des Nutzers, Tier-abhaengiges Limit)
  const fetchSwipeStatus = useCallback(async (): Promise<SwipeStatus | null> => {
    if (!activePregnancyId || !user) return null;

    const { data, error: statusError } = await supabase.rpc('get_swipe_status', {
      p_pregnancy_id: activePregnancyId,
      p_user_id: user.id,
      p_context_type: contextType,
    });

    if (statusError) throw statusError;

    return (data as SwipeStatus) ?? null;
  }, [activePregnancyId, user, contextType]);

  // Gesamtzahl der geswipten Namen (fuer die Statistik, nicht fuer das Limit)
  const fetchSwipeCount = useCallback(async () => {
    if (!activePregnancyId || !user) return 0;

    const { count, error: countError } = await supabase
      .from('name_swipes')
      .select('id', { count: 'exact', head: true })
      .eq('pregnancy_id', activePregnancyId)
      .eq('user_id', user.id);

    if (countError) throw countError;

    return count ?? 0;
  }, [activePregnancyId, user]);

  // Einen Batch noch nicht geswipter Namen vom Server holen
  const fetchBatch = useCallback(
    async (limit: number): Promise<BabyName[]> => {
      const { data, error: rpcError } = await supabase.rpc('get_unswiped_names', {
        p_pregnancy_id: activePregnancyId,
        p_user_id: user?.id,
        p_context_type: contextType,
        p_gender: filters.gender === 'all' ? null : filters.gender,
        p_origins: filters.origins.length > 0 ? filters.origins : null,
        p_starting_letter: filters.startingLetter,
        p_popularity_max: filters.popularityMax,
        p_limit: limit,
      });

      if (rpcError) throw rpcError;

      return (data || []) as BabyName[];
    },
    [activePregnancyId, user?.id, contextType, filters]
  );

  // Erstes Laden bzw. Neuladen nach Filterwechsel
  const fetchNames = useCallback(async () => {
    if (!activePregnancyId || !user) return;

    setIsLoading(true);
    setError(null);
    try {
      // Die Zaehler sind fuer die Anzeige der Namen nicht mehr noetig -
      // ein Fehler hier darf den Stapel nicht blockieren.
      try {
        const [status, count] = await Promise.all([fetchSwipeStatus(), fetchSwipeCount()]);
        setSwipeStatus(status);
        setSwipeCount(count);
        setLimitBlocked(false);
      } catch (countErr) {
        console.error('Error fetching swipe status:', countErr);
        setError(countErr instanceof Error ? countErr : new Error('Failed to fetch swipe status'));
      }

      const batch = await fetchBatch(BATCH_SIZE);
      setNames(batch);
      setCurrentIndex(0);
      setHasMoreFromServer(batch.length === BATCH_SIZE);
    } catch (err) {
      console.error('Error fetching names:', err);
      setError(err instanceof Error ? err : new Error('Failed to fetch names'));
    } finally {
      setIsLoading(false);
    }
  }, [activePregnancyId, user, fetchSwipeStatus, fetchSwipeCount, fetchBatch]);

  // Nachladen, ohne die sichtbare Karte anzufassen: kein isLoading, kein
  // Reset von currentIndex - der neue Batch wird nur angehaengt.
  const loadMoreNames = useCallback(
    async (remaining: number) => {
      if (isLoadingMoreRef.current) return;
      if (!activePregnancyId || !user) return;

      isLoadingMoreRef.current = true;
      try {
        // Der RPC kennt nur die bereits geswipten Namen. Die noch offenen
        // Namen im State liefert er erneut mit - deshalb entsprechend mehr
        // anfordern und beim Anhaengen deduplizieren.
        const requested = BATCH_SIZE + Math.max(0, remaining);
        const batch = await fetchBatch(requested);

        setNames((prev) => {
          const known = new Set(prev.map((n) => n.id));
          const fresh = batch.filter((n) => !known.has(n.id));
          return fresh.length > 0 ? [...prev, ...fresh] : prev;
        });

        // Kuerzere Antwort als angefragt => der Katalog ist fuer diese
        // Filter erschoepft. Die Ueberschneidung mit dem State ist immer
        // kleiner als BATCH_SIZE, eine volle Antwort bringt also stets
        // neue Namen mit.
        setHasMoreFromServer(batch.length >= requested);
      } catch (err) {
        console.error('Error loading more names:', err);
        setError(err instanceof Error ? err : new Error('Failed to load more names'));
        // Nicht endlos weiter versuchen - Retry laeuft ueber refetch.
        setHasMoreFromServer(false);
      } finally {
        isLoadingMoreRef.current = false;
      }
    },
    [activePregnancyId, user, fetchBatch]
  );

  // Initial fetch
  useEffect(() => {
    if (activePregnancyId) {
      fetchNames();
    }
  }, [activePregnancyId, fetchNames]);

  // Nachladen, sobald der Vorrat zur Neige geht
  useEffect(() => {
    if (isLoading || !hasMoreFromServer) return;

    const remaining = names.length - currentIndex;
    if (remaining < PREFETCH_THRESHOLD) {
      loadMoreNames(remaining);
    }
  }, [names.length, currentIndex, isLoading, hasMoreFromServer, loadMoreNames]);

  // Handle swipe action
  const handleSwipe = useCallback(
    async (nameId: string, swipeType: 'like' | 'dislike' | 'favorite') => {
      if (!activePregnancyId || !user) return;

      // Solange die Limits laden, ist der Tier noch unbekannt - erst danach
      // laesst sich sagen, ob ein Swipe ueberhaupt erlaubt ist.
      // Ist der Serverstand nicht abrufbar, wird der Swipe trotzdem
      // versucht: der Trigger entscheidet dann verbindlich, statt dass die
      // Karte ohne Rueckmeldung stehen bleibt.
      if (isLoadingLimits) return;

      // Check daily limit
      if (isLimitReached) {
        setLimitBlocked(true);
        return;
      }

      try {
        // Insert swipe. Das Limit wird zusaetzlich serverseitig per Trigger
        // durchgesetzt (Migration 160), der Client-Check ist nur die schnelle
        // Vorpruefung.
        const { error: swipeError } = await supabase.from('name_swipes').insert({
          pregnancy_id: activePregnancyId,
          user_id: user.id,
          name_id: nameId,
          swipe_type: swipeType,
          context_type: contextType,
        });

        // 23505: der Name wurde bereits geswipt. Der Swipe ist damit
        // faktisch gespeichert - weiterschalten statt auf der Karte haengen.
        if (swipeError && swipeError.code !== '23505') throw swipeError;

        // Update local state
        setSwipeCount((prev) => prev + 1);
        setSwipeStatus((prev) =>
          prev
            ? {
                ...prev,
                used_today: prev.used_today + 1,
                remaining: prev.is_unlimited ? null : Math.max(0, (prev.remaining ?? 0) - 1),
              }
            : prev
        );
        setCurrentIndex((prev) => prev + 1);

        // Check for match (if liked or favorited)
        if (swipeType !== 'dislike') {
          // Check if partner also liked this name
          const { data: partnerSwipe } = await supabase
            .from('name_swipes')
            .select('*')
            .eq('pregnancy_id', activePregnancyId)
            .eq('name_id', nameId)
            .neq('user_id', user.id)
            .in('swipe_type', ['like', 'favorite'])
            .maybeSingle();

          if (partnerSwipe) {
            // It's a match! Create match record
            const { data: match, error: matchError } = await supabase
              .from('name_matches')
              .insert({
                pregnancy_id: activePregnancyId,
                name_id: nameId,
                context_type: contextType,
              })
              .select('*, baby_name:baby_names(*)')
              .single();

            if (!matchError && match) {
              setNewMatch(match);
            }
          }
        }
      } catch (err) {
        // Der Trigger aus Migration 160 lehnt mit SWIPE_LIMIT_REACHED ab.
        // Das ist kein technischer Fehler, sondern der Normalfall bei
        // erreichtem Limit - und kann auch dann auftreten, wenn der lokale
        // Stand noch veraltet ist (zweites Geraet, Zeitzonenwechsel).
        const message = err instanceof Error ? err.message : String(err);
        if (message.includes(SWIPE_LIMIT_ERROR)) {
          setLimitBlocked(true);
          // Serverstand nachziehen, damit der Zaehler stimmt
          fetchSwipeStatus()
            .then((status) => { if (status) setSwipeStatus(status); })
            .catch(() => { /* Der blockierte Zustand steht bereits */ });
          return;
        }

        console.error('Error handling swipe:', err);
        setError(err instanceof Error ? err : new Error('Failed to save swipe'));
      }
    },
    [activePregnancyId, user, isLimitReached, contextType, isLoadingLimits, fetchSwipeStatus]
  );

  // Clear match notification
  const clearMatch = useCallback(() => {
    setNewMatch(null);
  }, []);

  // Update filters
  const updateFilters = useCallback((newFilters: Partial<NameFilters>) => {
    setFilters((prev) => ({ ...prev, ...newFilters }));
  }, []);

  // Reset filters
  const resetFilters = useCallback(() => {
    setFilters(DEFAULT_FILTERS);
  }, []);

  // Current name to display
  const currentName = names[currentIndex] || null;

  // Naechster Name - wird als Hintergrundkarte im Stapel gezeigt
  const nextName = names[currentIndex + 1] || null;

  // Check if more names available
  const hasMoreNames = currentIndex < names.length;

  return {
    currentName,
    nextName,
    hasMoreNames,
    // Solange die Limits laden, ist noch nicht entscheidbar, ob geswipt
    // werden darf - die Seite soll dann weiter den Ladezustand zeigen.
    isLoading: isLoading || isLoadingLimits,
    error: error || limitsError,
    swipeCount,
    dailySwipeCount,
    filters,
    newMatch,
    handleSwipe,
    clearMatch,
    updateFilters,
    resetFilters,
    refetch: fetchNames,
    // Limit info
    swipeLimit,
    hasUnlimitedSwipes,
    isLimitReached,
    remainingSwipes,
    resetsAt,
    // false, solange der Serverstand nicht geladen werden konnte - dann sind
    // swipeLimit/remainingSwipes 0 und duerfen nicht angezeigt werden
    hasSwipeStatus: swipeStatus !== null,
    // true, sobald der Server einen Swipe wegen des Limits abgelehnt hat
    limitBlocked,
    // Nachladen laeuft noch: von der UI gebraucht, um den Empty-State
    // nicht vorschnell zu zeigen (Phase 3)
    hasMoreFromServer,
  };
}
